import { createVersionCheck } from './utils/versionCheck';
import { routeLoaders, scheduleRoutePrefetch } from './routeLoaders';
import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppShell from './components/Layout/AppShell';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Onboarding from './pages/Onboarding';
import NotFound from './pages/NotFound';
import Dashboard from './pages/Dashboard';
import WhatsNew from './pages/WhatsNew';
import ClientDirectory from './pages/ClientDirectory';
import ClientDashboard from './pages/ClientDashboard';
import Facilities from './pages/Facilities';
import FacilityDetail from './pages/FacilityDetail';
import Inspections from './pages/Inspections';
import InspectionDetail from './pages/InspectionDetail';
import ActionsInbox from './pages/ActionsInbox';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import Invites from './pages/Invites';
import ClientPortalDashboard from './pages/ClientPortal/ClientPortalDashboard';
import ClientPortalActions from './pages/ClientPortal/ClientPortalActions';
import ClientPortalFacilities from './pages/ClientPortal/ClientPortalFacilities';
import ClientPortalInspections from './pages/ClientPortal/ClientPortalInspections';
import ClientPortalProfile from './pages/ClientPortal/ClientPortalProfile';
import './styles/globals.css';
import { AuthProvider } from './contexts/AuthContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import './styles/pages.css';

const AdminFeedback = lazy(routeLoaders.AdminFeedback);
const InspectionMode = lazy(routeLoaders.InspectionMode);
const Reports = lazy(routeLoaders.Reports);
const Settings = lazy(routeLoaders.Settings);
const AdminConsultancies = lazy(routeLoaders.AdminConsultancies);
const AdminLabReportsTest = lazy(routeLoaders.AdminLabReportsTest);
const AssetTypeRegistry = lazy(routeLoaders.AssetTypeRegistry);
const LabReports = lazy(routeLoaders.LabReports);
const LabReportDetail = lazy(routeLoaders.LabReportDetail);
const ClientPortalLabReports = lazy(routeLoaders.ClientPortalLabReports);
const ClientPortalWrmp = lazy(routeLoaders.ClientPortalWrmp);

/** Connect deploy detection to committed router navigations. */
function DeployCheck() {
  const location = useLocation();
  const checker = useRef(createVersionCheck());
  const previousLocation = useRef(location);
  useEffect(() => checker.current.start(), []);
  useEffect(() => {
    if (previousLocation.current !== location) checker.current.routeChanged();
    previousLocation.current = location;
  }, [location]);
  return null;
}

function App(): React.JSX.Element {
  useEffect(() => scheduleRoutePrefetch(), []);
  return (
    <AuthProvider>
      <ImpersonationProvider>
      <ViewModeProvider>
        <Router>
          <DeployCheck />
          <Suspense fallback={<p role="status">Loading…</p>}>
          <Routes>
            {/* Public — login + magic-link redirect */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* First-login onboarding — full-bleed, no AppShell. PrivateRoute
                still gates on session/profile, then lets Onboarding handle
                the rest of the redirect logic itself. */}
            <Route
              path="/onboarding"
              element={
                <PrivateRoute>
                  <Onboarding />
                </PrivateRoute>
              }
            />

            {/* Main Consultant/Admin App */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <AppShell />
                </PrivateRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="clients" element={<ClientDirectory />} />
              <Route path="clients/:id" element={<ClientDashboard />} />
              <Route path="facilities" element={<Facilities />} />
              <Route path="facilities/:id" element={<FacilityDetail />} />
              <Route path="inspections" element={<Inspections />} />
              <Route path="inspections/:id" element={<InspectionDetail />} />
              <Route path="actions" element={<ActionsInbox />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="reports" element={<Reports />} />
              <Route path="lab-reports" element={<LabReports />} />
              <Route path="lab-reports/:id" element={<LabReportDetail />} />
              <Route path="whats-new" element={<WhatsNew />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/asset-types" element={<AssetTypeRegistry />} />
              <Route path="invites" element={<Invites />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Full-screen routes outside AppShell */}
            <Route
              path="facilities/:id/inspection"
              element={
                <PrivateRoute>
                  <InspectionMode />
                </PrivateRoute>
              }
            />

            {/* Client Portal - shares the unified AppShell chrome */}
            <Route
              path="/portal"
              element={
                <PrivateRoute>
                  <AppShell />
                </PrivateRoute>
              }
            >
              <Route index element={<ClientPortalDashboard />} />
              <Route path="actions" element={<ClientPortalActions />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="facilities" element={<ClientPortalFacilities />} />
              <Route path="facilities/:id" element={<ClientPortalFacilities />} />
              <Route path="inspections" element={<ClientPortalInspections />} />
              <Route path="lab-reports" element={<ClientPortalLabReports />} />
              <Route path="lab-reports/:id" element={<LabReportDetail />} />
              <Route path="whats-new" element={<WhatsNew />} />
              <Route path="profile" element={<ClientPortalProfile />} />
              <Route path="wrmp" element={<ClientPortalWrmp />} />
              <Route path="wrmp/:facilityId" element={<ClientPortalWrmp />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* H2Okay admin shell — top-of-the-pyramid view that lists
                every consultancy and lets admins create new ones. Reuses
                the same AppShell chrome as the consultant app. */}
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AppShell />
                </PrivateRoute>
              }
            >
              <Route index element={<AdminConsultancies />} />
              <Route path="feedback" element={<AdminFeedback />} />
              <Route path="feedback/:id" element={<AdminFeedback />} />
              <Route path="whats-new" element={<WhatsNew />} />
              <Route path="profile" element={<Profile />} />
              <Route path="invites" element={<Invites />} />
              <Route path="lab-reports-test" element={<AdminLabReportsTest />} />
              <Route path="asset-types" element={<AssetTypeRegistry />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </Suspense>
        </Router>
      </ViewModeProvider>
      </ImpersonationProvider>
    </AuthProvider>
  );
}

export default App;
